import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { Image } from "react-bootstrap";
import DeleteIcon from "../../assets/icons/DeleteIcon.svg";
import plusBorderWhite from "../../assets/icons/Plus-border-white.svg";
import { API_URL } from "../../redux/constants/type";
import "../../assets/styles/local/ProductImagesBB.css";
import { fetchScreenTranslations } from "../../utils/helpers/translations";

const EquipmentImagesBB = ({ equipmentData, setEquipmentData }) => {
  const [projectListTranslation, setProjectListTranslation] = useState({});
  const t = (key) => projectListTranslation?.[key] || "";
  useEffect(() => {
    fetchScreenTranslations("Equipment").then((res) => {
      setProjectListTranslation(res);
    });
  }, []);

  const [productList, setProductList] = useState({
    coverimage: equipmentData?.picture || null,
    images: equipmentData?.equipmentimages || [],
  });

  const [hasEffectRun, setHasEffectRun] = useState(false);
  useEffect(() => {
    if (!hasEffectRun && equipmentData?.equipmentid) {
      setProductList({
        coverimage: equipmentData.picture || null,
        images: equipmentData.equipmentimages || [],
      });
      setHasEffectRun(true);
    }
  }, [equipmentData, hasEffectRun]);

  useEffect(() => {
    setEquipmentData((prevEquipmentData) => ({
      ...prevEquipmentData,
      equiptmentimages: productList.images,
    }));
  }, [productList.images, setEquipmentData]);

  useEffect(() => {
    setEquipmentData((prevEquipmentData) => ({
      ...prevEquipmentData,
      picture: productList.coverimage,
    }));
  }, [productList.coverimage, setEquipmentData]);

  const [disableSubImageCursor, setDisableSubImageCursor] = useState(
    !productList.coverimage
  );
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Initialize file input ref
    fileInputRef.current = document.createElement("input");
    fileInputRef.current.type = "file";
    fileInputRef.current.accept = "image/*";
    fileInputRef.current.style.display = "none";
    fileInputRef.current.addEventListener("change", handleCoverImageChange);
  }, []);

  const handleCoverImageClick = () => {
    fileInputRef.current.click();
    setDisableSubImageCursor(false); // Enable cursor when cover image is selected
  };

  const handleCoverImageChange = async (e) => {
    const selectedFile = e.target.files[0];
    // Check if selectedFile is present
    if (selectedFile) {
      // API call to upload the cover image
      try {
        const formData = new FormData();
        formData.append("file", selectedFile);
        const response = await axios.put(
          `${API_URL}/api/Files/UploadToS3`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        // Assuming the API response contains the image URL
        const imageUrl = response.data[0];

        // Update the cover image state only if the API call is successful
        setProductList((prevProductList) => ({
          ...prevProductList,
          coverimage: imageUrl,
        }));
        // Enable cursor when cover image is selected
        setDisableSubImageCursor(false);
      } catch (error) {
        // Handle error if necessary
      }
    } else {
      // Handle the case where no file is selected (optional)
    }
  };

  const handleCoverImageDrop = async (e) => {
    e.preventDefault();
    const selectedFile = e.dataTransfer.files[0];
    if (selectedFile) {
      try {
        const formData = new FormData();
        formData.append("file", selectedFile);
        const response = await axios.put(
          `${API_URL}/api/Files/UploadToS3`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        // Assuming the API response contains the image URL
        const imageUrl = response.data[0];

        // Update the cover image state only if the API call is successful
        setProductList((prevProductList) => ({
          ...prevProductList,
          coverimage: imageUrl,
        }));
        setDisableSubImageCursor(false); // Enable cursor when cover image is selected
      } catch (error) {
        // Handle error if necessary
      }
    } else {
      // Handle the case where no file is selected (optional)
    }
  };

  const handleSubImageClick = (index) => {
    // Check if coverImage is not null before allowing to select sub-images
    if (productList.coverimage !== null) {
      // Clicking the last input in the array
      const subImageInput = document.createElement("input");
      subImageInput.type = "file";
      subImageInput.accept = "image/*";
      subImageInput.style.display = "none";
      subImageInput.addEventListener("change", (e) =>
        handleSubImageChange(index, e)
      );
      subImageInput.click();
    } else {
      // You can display an alert or some indication to the user that they need to select a cover image first.
      setDisableSubImageCursor(true); // Disable cursor for sub-images
    }
  };

  const handleSubImageChange = async (index, e) => {
    const selectedFiles = e.target.files;
    // Check if any files were selected
    if (selectedFiles.length > 0) {
      try {
        const formData = new FormData();
        formData.append("file", selectedFiles[0]); // Assuming you want to upload only one file

        const response = await axios.put(
          `${API_URL}/api/Files/UploadToS3`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        // Assuming the API response contains the image URL
        const imageUrl = response.data[0];

        setProductList((prevProductList) => {
          const updatedImages = [...prevProductList.images];
          updatedImages[index] = {
            image: imageUrl,
          };
          return {
            ...prevProductList,
            images: updatedImages,
          };
        });
      } catch (error) {
        // Handle error if necessary
      }
    }
  };

  const handleDeleteSubImage = (index) => {
    const updatedSubImages = [...productList.images];
    updatedSubImages.splice(index, 1);
    setProductList((prevProductList) => ({
      ...prevProductList,
      images: updatedSubImages,
    }));
  };

  const handleDeleteCoverImage = () => {
    if (productList.images.length > 0) {
      // Move the first sub-image to coverImage
      const firstSubImage = productList?.images[0]?.image;
      // Remove the first sub-image from subImages
      const remainingSubImages = productList.images.slice(1);
      // Update the productList state
      setProductList((prevProductList) => ({
        ...prevProductList,
        coverimage: firstSubImage,
        images: remainingSubImages,
      }));
      setDisableSubImageCursor(false);
    } else {
      setDisableSubImageCursor(true); // Disable cursor for sub-images
      // Update the productList state if coverImage is set to null
      setProductList((prevProductList) => ({
        ...prevProductList,
        coverimage: null,
        images: [],
      }));
    }
  };

  const handleCoverImageDragOver = (e) => {
    e.preventDefault();
  };

  const handleRightWrapperDrop = async (e) => {
    if (productList.coverimage !== null) {
      e.preventDefault();
      const droppedFiles = e.dataTransfer.files;
      if (droppedFiles.length > 0) {
        try {
          const formData = new FormData();
          formData.append("file", droppedFiles[0]); // Assuming you only handle one file at a time
          const response = await axios.put(
            `${API_URL}/api/Files/UploadToS3`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
          // Assuming the API response contains the image URL
          const imageUrl = response.data[0];
          // Update the subImage state only if the API call is successful
          setProductList((prevProductList) => ({
            ...prevProductList,
            images: [...prevProductList.images, { image: imageUrl }],
          }));
        } catch (error) {}
      }
    }
  };

  const handleRightWrapperDragOver = (e) => {
    if (productList.coverimage !== null) {
      e.preventDefault();
    }
  };
  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const newImages = Array.from(productList.images);
    const [removed] = newImages.splice(result.source.index, 1);
    newImages.splice(result.destination.index, 0, removed);

    setProductList((prevProductList) => ({
      ...prevProductList,
      images: newImages,
    }));
  };
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="product-image-wrapper">
        <div
          className="product-image-left-wrp"
          onDrop={handleCoverImageDrop}
          onDragOver={handleCoverImageDragOver}
        >
          <div className="product-image-left" onClick={handleCoverImageClick}>
            <div
              className="default-img-left"
              onDrop={handleCoverImageDrop}
              onDragOver={handleCoverImageDragOver}
            >
              {productList.coverimage ? (
                <Image
                  src={productList.coverimage}
                  alt="Selected Image"
                  className="selected-img"
                />
              ) : (
                <Image height={12} width={12} src={plusBorderWhite} />
              )}
            </div>
          </div>
          <div className="image-text">
            <span onClick={handleCoverImageClick}>
              {/* Cover Image */}
              {t("Cover Image")}
            </span>
            <Image
              src={DeleteIcon}
              onClick={handleDeleteCoverImage}
              className="cursor-pointer"
            />
          </div>
        </div>
        <Droppable droppableId="subImages">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="product-image-right-wrp"
              onDrop={handleRightWrapperDrop}
              onDragOver={handleRightWrapperDragOver}
            >
              {Array.isArray(productList.images) &&
                productList.images.map((subImage, index) => (
                  <Draggable
                    key={index}
                    draggableId={`subImage-${index}`}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`sub-img ${
                          snapshot.isDragging ? "sub-img-dragging" : ""
                        } ${
                          disableSubImageCursor
                            ? "disable-sub-image-cursor"
                            : ""
                        }`}
                      >
                        <div
                          className="default-img"
                          onClick={() => handleSubImageClick(index)}
                        >
                          {subImage.image ? (
                            <Image
                              src={subImage.image}
                              alt={`Sub-Image ${index}`}
                              className="img-tag"
                            />
                          ) : (
                            <Image
                              height={12}
                              width={12}
                              src={plusBorderWhite}
                            />
                          )}
                        </div>
                        <Image
                          height={18}
                          width={16}
                          src={DeleteIcon}
                          className="sub-img-delete cursor-pointer"
                          onClick={() => handleDeleteSubImage(index)}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
              {provided.placeholder}
              <div
                className="sub-img-plus"
                onClick={() => handleSubImageClick(productList.images.length)}
              >
                <div
                  className={`default-img ${
                    // disableSubImageCursor ? "disable-sub-image-cursor" : ""
                    productList.coverimage === null
                      ? "disable-sub-image-cursor"
                      : ""
                  }`}
                >
                  <Image height={12} width={12} src={plusBorderWhite} />
                </div>
              </div>
            </div>
          )}
        </Droppable>
      </div>
    </DragDropContext>
  );
};

export default EquipmentImagesBB;
